import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import Logo from "./Logo";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.message || "Welcome to Veloura.");
            setEmail("");
        } catch {
            toast.error("Please enter a valid email.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer
            className="relative mt-32"
            style={{
                background: "var(--vl-bg)",
                borderTop: "1px solid var(--vl-border)",
            }}
            data-testid="footer"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <Logo
                        size="md"
                        variant="plate"
                        asLink={Link}
                        to="/"
                        testId="footer-logo"
                    />
                    <p
                        className="mt-6 text-sm max-w-sm"
                        style={{
                            color: "var(--vl-muted)",
                            lineHeight: 1.8,
                        }}
                    >
                        Intimate wellness, reimagined. Premium, discreet, and
                        designed for confidence, pleasure, and connection.
                    </p>
                    <form
                        onSubmit={subscribe}
                        className="mt-10 max-w-sm"
                        data-testid="newsletter-form-footer"
                    >
                        <div className="overline mb-3">
                            The Veloura Journal
                        </div>
                        <div className="flex items-end gap-3">
                            <input
                                type="email"
                                required
                                data-testid="newsletter-email-footer"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@private.com"
                                className="vl-input"
                            />
                            <button
                                type="submit"
                                data-testid="newsletter-submit-footer"
                                disabled={loading}
                                className="vl-btn"
                                style={{ padding: "10px 18px" }}
                            >
                                {loading ? "…" : "Join"}
                            </button>
                        </div>
                        <p
                            className="text-[11px] mt-3"
                            style={{ color: "var(--vl-muted)" }}
                        >
                            Unsubscribe anytime. We never share your email.
                        </p>
                    </form>
                </div>

                <div className="md:col-span-2">
                    <div className="overline mb-5">Shop</div>
                    <ul
                        className="space-y-3 text-sm"
                        style={{ color: "var(--vl-text)" }}
                    >
                        <li>
                            <Link
                                to="/shop"
                                data-testid="footer-shop-all"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                All Products
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/best-sellers"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                Best Sellers
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/categories"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                Categories
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/shop?category=luxury-collection"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                Luxury Collection
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="overline mb-5">Discover</div>
                    <ul
                        className="space-y-3 text-sm"
                        style={{ color: "var(--vl-text)" }}
                    >
                        <li>
                            <Link
                                to="/about"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                About Veloura
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/blog"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                The Journal
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/faq"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                FAQ
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="overline mb-5">Care</div>
                    <ul
                        className="space-y-3 text-sm"
                        style={{ color: "var(--vl-text)" }}
                    >
                        <li>
                            <Link
                                to="/contact"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                Contact concierge
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/track-order"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                Track your order
                            </Link>
                        </li>
                        <li>
                            <a
                                href="https://wa.me/919999999999"
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-[var(--vl-rose)]"
                            >
                                WhatsApp us
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div
                className="border-t"
                style={{ borderColor: "var(--vl-border)" }}
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p
                        className="text-[11px] tracking-widest uppercase"
                        style={{ color: "var(--vl-muted)" }}
                    >
                        © 2026 Veloura Wellness Atelier. All rights reserved.
                    </p>
                    <p
                        className="text-[11px] tracking-widest uppercase"
                        style={{ color: "var(--vl-muted)" }}
                    >
                        Shipped discreetly · Handled privately · Crafted
                        thoughtfully
                    </p>
                </div>
            </div>
        </footer>
    );
}
